import { Injectable } from '@angular/core';

import {
  Action,
  createSelector,
  Selector,
  SelectorOptions,
  State,
  StateContext,
} from '@ngxs/store';
import { patch, removeItem, updateItem } from '@ngxs/store/operators';
import {
  DataRelation,
  DataRelationControllerService,
  DataRelationDto,
} from '@osos/shared/api';
import {
  BreadcrumbToken,
  DataRelationPresenterModel,
  DataRelationTypeEnum,
  LoadingIndicator,
  Page,
} from '@osos/shared/types';
import { EMPTY, forkJoin, Observable, of } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import { DataRelationsFactoryService } from '../../factories/data-relations-factory/data-relations-factory.service';
import { ApiUtilsService, BreadcrumbService } from '../../services';
import { LoadingActions } from '../loading/loading.actions';
import { RelationActions } from './relation.action';

export interface RelationStateModel {
  page: Page<DataRelation>;
  presentedIncomingRelations?: DataRelationPresenterModel[];
  presentedOutgoingRelations?: DataRelationPresenterModel[];
  presentedRelation: { [key: string]: DataRelationPresenterModel };
  selectedRelation?: string; // key
  presentedIncomingRelationsCount: number;
  presentedOutgoingRelationsCount: number;
  joinerEntityRelations?: DataRelationPresenterModel[];
  /**
   * temporary state to store relations and select them once
   */
  transitoryIncomingRelations: DataRelationPresenterModel[];
  transitoryOutgoingRelations: DataRelationPresenterModel[];
  loading: LoadingIndicator;
}

@State<RelationStateModel>({
  name: 'dataRelations',
  defaults: {
    page: {
      items: [],
      totalCount: 0,
    },
    presentedIncomingRelations: [],
    presentedOutgoingRelations: [],
    transitoryIncomingRelations: [],
    transitoryOutgoingRelations: [],
    presentedRelation: {},
    joinerEntityRelations: [],
    presentedIncomingRelationsCount: 0,
    presentedOutgoingRelationsCount: 0,
    loading: new LoadingIndicator(),
  },
})
@Injectable()
@SelectorOptions({ injectContainerState: false })
export class RelationState {
  constructor(
    private dataRelationsService: DataRelationControllerService,
    private apiHelper: ApiUtilsService,
    private dataRelationsFactoryService: DataRelationsFactoryService,
    private breadcrumbService: BreadcrumbService,
  ) {}

  private getKey = RelationState.getKey;
  private static getKey(entityId: string, relationId: string): string {
    return `${entityId ?? ''}${relationId}`;
  }

  // --------------------------------- SELECTORS ------------------------------

  static presentedRelations(
    relationType: DataRelationTypeEnum,
  ): (state: RelationStateModel) => DataRelationPresenterModel[] {
    return createSelector([RelationState], (state: RelationStateModel) =>
      relationType === DataRelationTypeEnum.Incoming
        ? state.presentedIncomingRelations
        : state.presentedOutgoingRelations,
    );
  }

  static totalCount(
    relationType: DataRelationTypeEnum,
  ): (state: RelationStateModel) => number {
    return createSelector([RelationState], (state: RelationStateModel) =>
      relationType === DataRelationTypeEnum.Incoming
        ? state.presentedIncomingRelationsCount
        : state.presentedOutgoingRelationsCount,
    );
  }

  static presentedRelation(
    entityId: string,
    relationId: string,
  ): (state: RelationStateModel) => DataRelationPresenterModel {
    return createSelector(
      [RelationState],
      (state: RelationStateModel) =>
        state.presentedRelation[RelationState.getKey(entityId, relationId)],
    );
  }

  @Selector([RelationState])
  static selectedRelation(
    state: RelationStateModel,
  ): DataRelationPresenterModel {
    return state.presentedRelation[state.selectedRelation];
  }

  /**
   * select transitory relations.
   * it's valid to be read once after trigger TransitoryFetchRelations action
   */
  @Selector([RelationState])
  static transitoryRelations(
    state: RelationStateModel,
  ): DataRelationPresenterModel[] {
    return state.transitoryIncomingRelations.concat(
      state.transitoryOutgoingRelations,
    );
  }
  @Selector([RelationState])
  static transitoryIncomingRelations(
    state: RelationStateModel,
  ): DataRelationPresenterModel[] {
    return state.transitoryIncomingRelations;
  }
  @Selector([RelationState])
  static transitoryOutgoingRelations(
    state: RelationStateModel,
  ): DataRelationPresenterModel[] {
    return state.transitoryOutgoingRelations;
  }

  @Selector([RelationState])
  static joinerEntityRelations(
    state: RelationStateModel,
  ): DataRelationPresenterModel[] {
    return state.joinerEntityRelations;
  }
  // --------------------------------- ACTIONS ------------------------------

  @Action(RelationActions.SelectRelation)
  selectRelation(
    { patchState }: StateContext<RelationStateModel>,
    { entityId, relationId }: RelationActions.SelectRelation,
  ): void {
    const selectedRelationKey = this.getKey(entityId, relationId);

    patchState({
      selectedRelation: selectedRelationKey,
    });
  }

  @Action(RelationActions.FetchRelations, { cancelUncompleted: true })
  fetchRelations(
    { setState, dispatch }: StateContext<RelationStateModel>,
    { payload }: RelationActions.FetchRelations,
  ): any {
    const where = {};
    dispatch(new LoadingActions.SetLoading(true));
    setState(
      patch<RelationStateModel>({
        loading: patch<LoadingIndicator>({
          page: true,
        }),
        presentedIncomingRelations: [],
        presentedOutgoingRelations: [],
      }),
    );

    return forkJoin([
      this.dataRelationsService.findV2({
        entity: payload?.entityId,
        ...this.apiHelper.prepareFilter({
          where,
          limit: payload?.limit ?? Number.MAX_SAFE_INTEGER,
          skip: payload?.skip ?? 0,
        }),
      }),
      payload.withCount
        ? this.dataRelationsService.countV2({ entity: payload.entityId })
        : of({ incoming: 0, outgoing: 0 }),
    ]).pipe(
      map(([relations, count]) => {
        setState(
          patch<RelationStateModel>({
            presentedIncomingRelations: relations.incoming?.map((r) =>
              this.dataRelationsFactoryService.createPresenterModel(r),
            ),
            presentedOutgoingRelations: relations.outgoing?.map((r) =>
              this.dataRelationsFactoryService.createPresenterModel(r),
            ),
            presentedIncomingRelationsCount: count.incoming,
            presentedOutgoingRelationsCount: count.outgoing,
          }),
        );
      }),
      finalize(() => {
        dispatch(new LoadingActions.SetLoading(false));
        setState(
          patch<RelationStateModel>({
            loading: patch<LoadingIndicator>({
              page: false,
            }),
          }),
        );
      }),
    );
  }

  @Action(RelationActions.TransitoryFetchRelations)
  transitoryFetchRelations(
    ctx: StateContext<RelationStateModel>,
    { entityId }: RelationActions.TransitoryFetchRelations,
  ) {
    return this.dataRelationsService.findV2({ entity: entityId }).pipe(
      tap((res) => {
        ctx.patchState({
          transitoryIncomingRelations: res.incoming.map((dataRelation) =>
            this.dataRelationsFactoryService.createPresenterModel(dataRelation),
          ),
          transitoryOutgoingRelations: res.outgoing.map((dataRelation) =>
            this.dataRelationsFactoryService.createPresenterModel(dataRelation),
          ),
        });
      }),
    );
  }

  @Action(RelationActions.FetchRelation)
  fetchRelation(
    { getState, setState }: StateContext<RelationStateModel>,
    { entityId, relationId }: RelationActions.FetchRelation,
  ): Observable<DataRelationDto> {
    const relation =
      getState().presentedRelation[this.getKey(entityId, relationId)];

    if (relation) {
      return EMPTY;
    }

    setState(
      patch<RelationStateModel>({
        loading: patch<LoadingIndicator>({
          item: true,
        }),
      }),
    );

    return this.dataRelationsService
      .findById({ entity: entityId, id: relationId })
      .pipe(
        tap((res) => {
          const presenter =
            this.dataRelationsFactoryService.createPresenterModel(res);

          this.breadcrumbService.set(
            BreadcrumbToken.RelationName,
            presenter.name,
          );
          setState(
            patch<RelationStateModel>({
              presentedRelation: patch({
                [this.getKey(entityId, relationId)]: presenter,
              }),
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<RelationStateModel>({
              loading: patch<LoadingIndicator>({
                item: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(RelationActions.CreateRelation)
  createRelation(
    { setState, dispatch }: StateContext<RelationStateModel>,
    { entityId, relation }: RelationActions.CreateRelation,
  ): Observable<DataRelation> {
    dispatch(new LoadingActions.SetCommandLoading(true));

    setState(
      patch<RelationStateModel>({
        loading: patch<LoadingIndicator>({
          create: true,
        }),
      }),
    );

    return this.dataRelationsService
      .create({
        entity: entityId,
        body: this.dataRelationsFactoryService.createDataModel(relation),
      })
      .pipe(
        switchMap((res) =>
          this.dataRelationsService.findById({
            entity: entityId,
            id: res.id,
          }),
        ),
        tap((res) => {
          const presenter =
            this.dataRelationsFactoryService.createPresenterModel(res);
          setState(
            patch<RelationStateModel>({
              presentedRelation: patch({
                [this.getKey(entityId, res.id)]: presenter,
              }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<RelationStateModel>({
              loading: patch<LoadingIndicator>({
                create: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(RelationActions.UpdateRelation)
  updateRelation(
    { setState, dispatch }: StateContext<RelationStateModel>,
    { entityId, relation }: RelationActions.UpdateRelation,
  ): Observable<DataRelationDto> {
    setState(
      patch<RelationStateModel>({
        loading: patch<LoadingIndicator>({
          update: true,
        }),
      }),
    );
    dispatch(new LoadingActions.SetCommandLoading(true));

    return this.dataRelationsService
      .updateById({
        entity: entityId,
        id: relation.id,
        body: this.dataRelationsFactoryService.createDataModel(relation),
      })
      .pipe(
        switchMap(() =>
          this.dataRelationsService.findById({
            entity: entityId,
            id: relation.id,
          }),
        ),
        tap((res) => {
          const presenter =
            this.dataRelationsFactoryService.createPresenterModel(res);
          setState(
            patch<RelationStateModel>({
              presentedRelation: patch({
                [this.getKey(entityId, relation.id)]: presenter,
              }),
              presentedIncomingRelations: updateItem(
                (r) => r.id === relation.id,
                presenter,
              ),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<RelationStateModel>({
              loading: patch<LoadingIndicator>({
                update: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(RelationActions.DeleteRelation)
  deleteRelation(
    { setState, dispatch, getState }: StateContext<RelationStateModel>,
    { entityId, relationId }: RelationActions.DeleteRelation,
  ): Observable<void> {
    setState(
      patch<RelationStateModel>({
        loading: patch<LoadingIndicator>({
          remove: true,
        }),
      }),
    );
    dispatch(new LoadingActions.SetCommandLoading(true));
    const count = getState().presentedIncomingRelationsCount - 1;
    return this.dataRelationsService
      .deleteById({ entity: entityId, id: relationId })
      .pipe(
        tap(() => {
          setState(
            patch<RelationStateModel>({
              page: patch<Page<DataRelation>>({
                items: removeItem((r) => r.id === relationId),
                totalCount: (c) => c--,
              }),
              presentedIncomingRelations: removeItem(
                (r) => r.id === relationId,
              ),
              presentedIncomingRelationsCount: count,
              presentedRelation: patch({
                [this.getKey(entityId, relationId)]: undefined,
              }),
            }),
          );
        }),
        finalize(() => {
          dispatch(new LoadingActions.SetCommandLoading(false));

          setState(
            patch<RelationStateModel>({
              loading: patch<LoadingIndicator>({
                remove: false,
              }),
            }),
          );
        }),
      );
  }

  @Action(RelationActions.FetchJoinerEntityRelations)
  fetchJoinerEntityRelation(
    { setState, getState }: StateContext<RelationStateModel>,
    { entitiesIds, operation }: RelationActions.FetchJoinerEntityRelations,
  ): Observable<any> {
    const existingRelations =
      operation === 'fetch' ? [] : getState().joinerEntityRelations;
    return this.dataRelationsService
      .findEntitiesRelations({ body: { dataEntities: entitiesIds } })
      .pipe(
        map((res) =>
          res.map((relation) =>
            this.dataRelationsFactoryService.createPresenterModel(relation),
          ),
        ),
        tap((res) => {
          setState(
            patch<RelationStateModel>({
              joinerEntityRelations: [...existingRelations, ...res],
            }),
          );
        }),
        finalize(() => {
          setState(
            patch<RelationStateModel>({
              loading: patch<LoadingIndicator>({
                remove: false,
              }),
            }),
          );
        }),
      );
  }
}
